import { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, ExternalLink, Loader2, ArrowRight, AlertTriangle, Repeat, Trash2 } from 'lucide-react';
import { apiCall } from './useAutoSave';
import { fmtCena } from './receptUtils';
import TypBadge from './TypBadge';
import { Z_NAD_LISTOU } from '../ui/ModalShell';
import { getGlassSurface } from '../../pages/MojeSmena/sheetStyle';
import useJeMobil from '../../hooks/useJeMobil';

const ACCENT = '#EF9F27';

// Kam vede receptura ze seznamu použití — záložku vybere cílová stránka podle typu
const cilReceptu = (r) => ['produkt-kvalita', { polozka: { typ: r.typ, id: r.id } }];

/**
 * Kde se položka používá + hromadná náhrada.
 *
 * Do teď u položky svítilo jen číslo („použito 97×"), ne kde. Modal ukáže
 * seznam receptur a umí vybrané řádky přepsat na jinou položku — s náhledem,
 * co to udělá s nákladem, než se cokoliv uloží.
 */
export default function PouzitiModal({ typ, id, isDark, onClose, onNavigate, onZmena }) {
    const isMobile = useJeMobil();
    const [data, setData] = useState(null);
    const [chyba, setChyba] = useState('');
    const [vybrane, setVybrane] = useState(() => new Set());
    // Řádky balicích předpisů mají vlastní číselnou řadu, proto druhá množina.
    const [vybraneP, setVybraneP] = useState(() => new Set());

    // Náhrada
    const [hledani, setHledani] = useState('');
    const [vysledky, setVysledky] = useState([]);
    const [cil, setCil] = useState(null);
    const [rezim, setRezim] = useState('nahrada');   // 'nahrada' | 'odebrani'
    const [nahled, setNahled] = useState(null);
    const [pocita, setPocita] = useState(false);
    const [uklada, setUklada] = useState(false);
    const debounceRef = useRef(null);

    const secondaryColor = isDark ? 'rgba(255,255,255,0.55)' : 'var(--text-secondary)';
    const mutedColor = isDark ? 'rgba(255,255,255,0.35)' : 'var(--text-muted)';
    const borderColor = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';
    const inputStyle = {
        background: isDark ? 'rgba(255,255,255,0.06)' : 'var(--surface-base)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`,
        color: 'var(--text-primary)', borderRadius: 6, padding: '8px 12px 8px 32px',
        fontSize: 13, width: '100%', outline: 'none',
    };

    // ── Načtení použití ──
    useEffect(() => {
        let zije = true;
        setData(null); setChyba('');
        apiCall(`/api/recepty/pouziti/${typ}/${id}`)
            .then(d => {
                if (!zije) return;
                setData(d);
                // Neaktivní receptury se ukážou, ale do náhrady se samy nepřidají.
                setVybrane(new Set(d.pouziti.filter(p => p.recept.aktivni).map(p => p.kusovnik_id)));
                setVybraneP(new Set((d.predpisy || []).filter(p => p.predpis.aktivni).map(p => p.polozka_id)));
            })
            .catch(e => zije && setChyba(e?.error || 'Použití se nepodařilo načíst'));
        return () => { zije = false; };
    }, [typ, id]);

    // ── Našeptávač cílové položky ──
    useEffect(() => {
        if (hledani.length < 1) { setVysledky([]); return; }
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            try {
                const r = await apiCall(`/api/recepty/materialy/search?q=${encodeURIComponent(hledani)}`);
                setVysledky(r.filter(x => !(x.typ === typ && x.id === id)));   // sebe sama ne
            } catch { setVysledky([]); }
        }, 300);
        return () => clearTimeout(debounceRef.current);
    }, [hledani, typ, id]);

    // ── Náhled dopadu ──
    const odebrani = rezim === 'odebrani';

    useEffect(() => {
        if ((!cil && !odebrani) || (vybrane.size === 0 && vybraneP.size === 0)) { setNahled(null); return; }
        let zije = true;
        setPocita(true);
        apiCall('/api/recepty/pouziti/nahrada/nahled', 'POST', {
            z: { typ, id },
            na: odebrani ? null : { typ: cil.typ, id: cil.id },
            kusovnik_ids: [...vybrane],
            predpis_polozka_ids: [...vybraneP],
        })
            .then(d => zije && setNahled(d))
            .catch(e => zije && setChyba(e?.error || 'Náhled se nepodařilo spočítat'))
            .finally(() => zije && setPocita(false));
        return () => { zije = false; };
    }, [cil, odebrani, vybrane, vybraneP, typ, id]);

    const pouziti = data?.pouziti || [];
    const predpisy = data?.predpisy || [];
    const receptur = useMemo(() => new Set(pouziti.map(p => p.recept.id)).size, [pouziti]);
    const planPodleRadku = useMemo(() => {
        const m = new Map();
        for (const r of nahled?.radky || []) m.set(r.kusovnik_id, r);
        return m;
    }, [nahled]);

    const prepniP = (pid) => setVybraneP(prev => {
        const n = new Set(prev);
        n.has(pid) ? n.delete(pid) : n.add(pid);
        return n;
    });

    const prepni = (kid) => setVybrane(prev => {
        const n = new Set(prev);
        n.has(kid) ? n.delete(kid) : n.add(kid);
        return n;
    });

    const provest = async () => {
        if ((!cil && !odebrani) || (vybrane.size === 0 && vybraneP.size === 0)) return;
        const kolikReceptur = nahled?.receptury?.length ?? '?';
        const potvrzeni = odebrani
            ? `Odebrat položku z ${vybrane.size} řádků v ${kolikReceptur} recepturách? Řádky se z kusovníků smažou.`
            : `Přepsat ${vybrane.size} řádků v ${kolikReceptur} recepturách na „${cil.nazev}"?`;
        if (!confirm(potvrzeni)) return;
        setUklada(true);
        try {
            await apiCall('/api/recepty/pouziti/nahrada', 'POST', {
                z: { typ, id },
                na: odebrani ? null : { typ: cil.typ, id: cil.id },
                kusovnik_ids: [...vybrane],
                predpis_polozka_ids: [...vybraneP],
            });
            onZmena?.();
            onClose();
        } catch (e) {
            setChyba(e?.error || (odebrani ? 'Odebrání se nepovedlo' : 'Náhrada se nepovedla'));
        }
        setUklada(false);
    };

    const th = { padding: '6px 8px', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em', color: mutedColor, fontWeight: 600, textAlign: 'left' };

    /* Portál do body: modal se otevírá ze stálého panelu karty, a ten má sklo
       (`backdrop-filter`). Bez portálu by se `position: fixed` vázalo na panel
       místo na výřez a modal by vyjel mimo střed. Na mobilu sedí u dolní hrany
       a zaobluje jen horní rohy — stejně jako sheety v Přehledu. */
    return createPortal((
        <div onClick={onClose}
            style={{ position: 'fixed', inset: 0, zIndex: Z_NAD_LISTOU, display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
            <div onClick={e => e.stopPropagation()}
                style={{
                    width: isMobile ? '100%' : 900, maxWidth: isMobile ? '100%' : '95vw',
                    maxHeight: isMobile ? '88dvh' : '88vh',
                    borderRadius: isMobile ? '20px 20px 0 0' : 12,
                    display: 'flex', flexDirection: 'column', overflow: 'hidden',
                    ...getGlassSurface(isDark), boxShadow: '0 24px 64px rgba(0,0,0,0.35)',
                }}>
                {/* Hlavička */}
                <div className="flex items-center gap-3 px-5 py-4" style={{ borderBottom: `1px solid ${borderColor}` }}>
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                            <TypBadge typ={typ} />
                            <span className="text-[15px] font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
                                {data?.polozka?.nazev || '…'}
                            </span>
                        </div>
                        <div className="text-[11px] mt-0.5" style={{ color: secondaryColor }}>
                            {data
                                ? [pouziti.length ? `${pouziti.length} řádků v ${receptur} recepturách` : null,
                               predpisy.length ? `${predpisy.length} řádků v balicích předpisech` : null]
                                .filter(Boolean).join(' · ') || 'zatím nikde'
                                : 'Načítám použití…'}
                        </div>
                    </div>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor }}>
                        <X size={18} />
                    </button>
                </div>

                {chyba && (
                    <div className="px-5 py-2 text-xs" style={{ background: 'rgba(239,68,68,0.10)', color: '#EF4444' }}>{chyba}</div>
                )}

                {/* Náhrada — výběr cílové položky */}
                <div className="px-5 py-3 flex items-center gap-3" style={{ borderBottom: `1px solid ${borderColor}` }}>
                    {odebrani ? <Trash2 size={14} style={{ color: '#EF4444', flexShrink: 0 }} /> : <Repeat size={14} style={{ color: mutedColor, flexShrink: 0 }} />}
                    <span className="text-xs flex-shrink-0" style={{ color: secondaryColor }}>
                        {odebrani ? 'Odebrat bez náhrady' : 'Nahradit za'}
                    </span>
                    {odebrani ? (
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                            <span className="text-xs" style={{ color: mutedColor }}>
                                Vybrané řádky se z kusovníků smažou. Receptury zůstanou, jen bez téhle položky.
                            </span>
                            <button onClick={() => { setRezim('nahrada'); setNahled(null); }}
                                className="text-[11px] flex-shrink-0" style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor, textDecoration: 'underline' }}>
                                zpět na náhradu
                            </button>
                        </div>
                    ) : cil ? (
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                            <TypBadge typ={cil.typ} />
                            <span className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>{cil.nazev}</span>
                            <span className="text-[11px]" style={{ color: mutedColor }}>{cil.kod}</span>
                            <button onClick={() => { setCil(null); setNahled(null); setHledani(''); }}
                                className="text-[11px]" style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor, textDecoration: 'underline' }}>
                                změnit
                            </button>
                        </div>
                    ) : (
                        <div className="relative flex-1 min-w-0">
                            <Search size={13} style={{ position: 'absolute', left: 10, top: 10, color: mutedColor }} />
                            <input value={hledani} onChange={e => setHledani(e.target.value)}
                                placeholder="Hledat surovinu, obal, spotřební zboží nebo polotovar…" style={inputStyle} />
                            {vysledky.length > 0 && (
                                <div className="absolute left-0 right-0 z-50 mt-1 rounded-lg overflow-hidden"
                                    style={{ background: isDark ? '#1e1e2e' : '#fff', border: `1px solid ${borderColor}`, boxShadow: '0 8px 24px rgba(0,0,0,0.18)', maxHeight: 260, overflowY: 'auto' }}>
                                    {vysledky.map(v => (
                                        <button key={`${v.typ}-${v.id}`} onClick={() => { setCil(v); setVysledky([]); setHledani(''); }}
                                            className="w-full flex items-center gap-2 px-3 py-2 text-left"
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-primary)', fontSize: 13 }}
                                            onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'}
                                            onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                                            <TypBadge typ={v.typ} />
                                            <span className="flex-1 truncate">{v.nazev}</span>
                                            <span className="text-[11px]" style={{ color: mutedColor }}>{v.kod}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                    {!odebrani && !cil && (
                        <button onClick={() => { setRezim('odebrani'); setCil(null); setHledani(''); setVysledky([]); }}
                            className="text-[11px] flex-shrink-0" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', textDecoration: 'underline' }}>
                            nebo odebrat bez náhrady
                        </button>
                    )}
                    {pocita && <Loader2 size={14} className="animate-spin" style={{ color: mutedColor }} />}
                </div>

                {/* Varování z náhledu */}
                {nahled && (
                    <div className="px-5 py-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px]" style={{ borderBottom: `1px solid ${borderColor}` }}>
                        {nahled.odebrani && (
                            <span style={{ color: '#EF4444' }}><AlertTriangle size={11} style={{ display: 'inline', marginRight: 4 }} />
                                Řádky se z kusovníků smažou — vrátit to jde jen ručním přidáním zpátky
                            </span>
                        )}
                        {nahled.cena_chybi && (
                            <span style={{ color: '#EF4444' }}><AlertTriangle size={11} style={{ display: 'inline', marginRight: 4 }} />
                                Cílová položka nemá platnou cenu — do kalkulace vstoupí nulou
                            </span>
                        )}
                        {nahled.jednotek_zmeneno > 0 && (
                            <span style={{ color: '#F97316' }}>
                                <AlertTriangle size={11} style={{ display: 'inline', marginRight: 4 }} />
                                U {nahled.jednotek_zmeneno} řádků cílová položka neumí zadanou jednotku — přepnou se na {nahled.na.jednotka}, množství zůstane. Zkontroluj je.
                            </span>
                        )}
                        {nahled.duplicit > 0 && (
                            <span style={{ color: secondaryColor }}>{nahled.duplicit}× je cílová položka v tom kroku už zadaná — spotřeba se sečte do jednoho řádku</span>
                        )}
                        {nahled.nadrazenych_receptur > 0 && (
                            <span style={{ color: secondaryColor }}>Změna se promítne i do {nahled.nadrazenych_receptur} receptur, které dotčené polotovary obsahují</span>
                        )}
                    </div>
                )}

                {/* Seznam použití */}
                <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }} className="px-5 py-3">
                    {!data ? (
                        <div className="flex items-center justify-center py-12" style={{ color: mutedColor }}>
                            <Loader2 size={18} className="animate-spin" />
                        </div>
                    ) : pouziti.length === 0 && predpisy.length === 0 ? (
                        <p className="text-sm text-center py-10" style={{ color: mutedColor }}>Položka zatím nikde nefiguruje.</p>
                    ) : (
                        <>
                        {predpisy.length > 0 && (
                            <div className="mb-4">
                                <div className="text-[10px] uppercase tracking-wide font-semibold mb-1" style={{ color: mutedColor }}>
                                    Balicí předpisy
                                </div>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <tbody>
                                        {predpisy.map(p => {
                                            const dopad = nahled?.predpisy?.find(x => x.polozka_id === p.polozka_id);
                                            return (
                                                <tr key={p.polozka_id} style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}`, opacity: p.predpis.aktivni ? 1 : 0.55 }}>
                                                    <td style={{ padding: '6px 8px', width: 28 }}>
                                                        <input type="checkbox" checked={vybraneP.has(p.polozka_id)}
                                                            onChange={() => prepniP(p.polozka_id)}
                                                            style={{ cursor: 'pointer', accentColor: ACCENT }} />
                                                    </td>
                                                    <td style={{ padding: '6px 8px' }}>
                                                        <div className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>{p.predpis.nazev}</div>
                                                        <div className="text-[10px]" style={{ color: mutedColor }}>
                                                            {p.predpis.kod} · nese {p.predpis.polozek} položek
                                                        </div>
                                                    </td>
                                                    <td style={{ padding: '6px 8px', fontSize: 11, color: secondaryColor }}>{p.kanal_nazev}</td>
                                                    <td style={{ padding: '6px 8px', fontSize: 12, textAlign: 'right', color: 'var(--text-primary)' }}>
                                                        {p.spotreba} {p.jednotka}
                                                    </td>
                                                    <td style={{ padding: '6px 8px', fontSize: 12, textAlign: 'right', color: 'var(--text-primary)' }}>
                                                        {fmtCena(p.naklad)}
                                                    </td>
                                                    {nahled && (
                                                        <td style={{ padding: '6px 8px', fontSize: 11, textAlign: 'right' }}>
                                                            {vybraneP.has(p.polozka_id) && dopad ? (
                                                                <span style={{ color: dopad.rozdil > 0 ? '#EF4444' : dopad.rozdil < 0 ? '#10B981' : mutedColor }}>
                                                                    {dopad.naklad_pred} → {dopad.naklad_po}
                                                                    <span className="ml-1 font-semibold">{dopad.rozdil > 0 ? '+' : ''}{dopad.rozdil}</span>
                                                                </span>
                                                            ) : <span style={{ color: mutedColor }}>—</span>}
                                                        </td>
                                                    )}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {pouziti.length > 0 && (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ borderBottom: `1px solid ${borderColor}` }}>
                                    <th style={{ ...th, width: 28 }}></th>
                                    <th style={th}>Receptura</th>
                                    <th style={th}>Krok</th>
                                    <th style={{ ...th, textAlign: 'right' }}>Množství</th>
                                    <th style={{ ...th, textAlign: 'right' }}>Náklad</th>
                                    {nahled && <th style={{ ...th, textAlign: 'right' }}>{odebrani ? 'Po odebrání' : 'Po náhradě'}</th>}
                                    <th style={{ ...th, width: 28 }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {pouziti.map(p => {
                                    const dopad = nahled?.receptury?.find(r => r.recept_id === p.recept.id);
                                    const plan = planPodleRadku.get(p.kusovnik_id);
                                    const zaskrtnuto = vybrane.has(p.kusovnik_id);
                                    return (
                                        <tr key={p.kusovnik_id}
                                            style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}`, opacity: p.recept.aktivni ? 1 : 0.55 }}>
                                            <td style={{ padding: '6px 8px' }}>
                                                <input type="checkbox" checked={zaskrtnuto} onChange={() => prepni(p.kusovnik_id)}
                                                    style={{ cursor: 'pointer', accentColor: ACCENT }} />
                                            </td>
                                            <td style={{ padding: '6px 8px' }}>
                                                <div className="flex items-center gap-1.5">
                                                    <TypBadge typ={p.recept.typ} />
                                                    <span className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>{p.recept.nazev}</span>
                                                    {p.recept.varianta && <span className="text-[10px]" style={{ color: mutedColor }}>{p.recept.varianta}</span>}
                                                    {!p.recept.aktivni && (
                                                        <span className="text-[9px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(148,163,184,0.18)', color: mutedColor }}>neaktivní</span>
                                                    )}
                                                </div>
                                                <div className="text-[10px]" style={{ color: mutedColor }}>{p.recept.kod}</div>
                                            </td>
                                            <td style={{ padding: '6px 8px', fontSize: 11, color: secondaryColor }}>
                                                {p.krok ? p.krok.nazev : <span style={{ color: '#EF4444' }}>nepřiřazeno</span>}
                                            </td>
                                            <td style={{ padding: '6px 8px', fontSize: 12, textAlign: 'right', color: 'var(--text-primary)' }}>
                                                {p.spotreba} {p.jednotka}
                                                {plan && !plan.jednotka_ok && (
                                                    <span style={{ color: '#F97316' }}> → {nahled.na.jednotka}</span>
                                                )}
                                            </td>
                                            <td style={{ padding: '6px 8px', fontSize: 12, textAlign: 'right', color: 'var(--text-primary)' }}>
                                                {p.naklad != null ? fmtCena(p.naklad) : '—'}
                                            </td>
                                            {nahled && (
                                                <td style={{ padding: '6px 8px', fontSize: 11, textAlign: 'right' }}>
                                                    {zaskrtnuto && dopad ? (
                                                        <span style={{ color: dopad.rozdil > 0 ? '#EF4444' : dopad.rozdil < 0 ? '#10B981' : mutedColor }}>
                                                            {dopad.naklad_pred} → {dopad.naklad_po}
                                                            <span className="ml-1 font-semibold">
                                                                {dopad.rozdil > 0 ? '+' : ''}{dopad.rozdil}
                                                            </span>
                                                        </span>
                                                    ) : <span style={{ color: mutedColor }}>—</span>}
                                                </td>
                                            )}
                                            <td style={{ padding: '6px 8px' }}>
                                                {onNavigate && (
                                                    <button onClick={() => { const [p2, q] = cilReceptu(p.recept); onNavigate(p2, q); onClose(); }}
                                                        title="Otevřít recepturu"
                                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor, padding: 0 }}>
                                                        <ExternalLink size={13} />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        )}
                        </>
                    )}
                </div>

                {/* Patička */}
                <div className="flex items-center justify-between px-5 py-3" style={{ borderTop: `1px solid ${borderColor}` }}>
                    <div className="text-[11px]" style={{ color: secondaryColor }}>
                        {(pouziti.length + predpisy.length) > 0 && (
                            <>
                                Vybráno {vybrane.size + vybraneP.size} z {pouziti.length + predpisy.length} řádků
                                <button onClick={() => { setVybrane(new Set(pouziti.map(p => p.kusovnik_id))); setVybraneP(new Set(predpisy.map(p => p.polozka_id))); }}
                                    className="ml-2" style={{ background: 'none', border: 'none', cursor: 'pointer', color: ACCENT, textDecoration: 'underline' }}>vše</button>
                                <button onClick={() => { setVybrane(new Set()); setVybraneP(new Set()); }}
                                    className="ml-2" style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor, textDecoration: 'underline' }}>nic</button>
                            </>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={onClose}
                            style={{ background: 'none', border: `1px solid ${borderColor}`, borderRadius: 6, padding: '7px 14px', fontSize: 13, cursor: 'pointer', color: secondaryColor }}>
                            Zavřít
                        </button>
                        {(() => {
                            const lze = (odebrani || cil) && (vybrane.size + vybraneP.size) > 0;
                            return (
                                <button onClick={provest} disabled={!lze || pocita || uklada}
                                    className="flex items-center gap-1.5"
                                    style={{
                                        background: !lze ? (isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)') : (odebrani ? '#EF4444' : ACCENT),
                                        color: !lze ? mutedColor : (odebrani ? '#fff' : '#412402'),
                                        border: 'none', borderRadius: 6, padding: '7px 14px', fontSize: 13, fontWeight: 600,
                                        cursor: (!lze || uklada) ? 'default' : 'pointer',
                                    }}>
                                    {uklada ? <Loader2 size={13} className="animate-spin" /> : odebrani ? <Trash2 size={13} /> : <ArrowRight size={13} />}
                                    {odebrani ? 'Odebrat' : 'Nahradit'} {(vybrane.size + vybraneP.size) > 0 ? `(${vybrane.size + vybraneP.size})` : ''}
                                </button>
                            );
                        })()}
                    </div>
                </div>
            </div>
        </div>
    ), document.body);
}
